import { openRazorpay, type RazorpayOptions } from '@/features/payments/gateways/razorpay'
import { openCashfree } from '@/features/payments/gateways/cashfree'
import { openPaddle } from '@/features/payments/gateways/paddle'
import type { GatewayResult } from '@/features/payments/gateways/types'
import type { OnlinePaymentResponse, RazorpayOrderInit, SubscriptionUpgradeResponse } from '@/types/models'
import { useSessionStore } from '@/stores/session.store'

export * from '@/features/payments/gateways/types'
export { openRazorpay, openCashfree, openPaddle }

// The order's own currency (sent by the backend since LP-492); an older backend sends none, so the caller passes the
// lease's. INR only when neither is known.
export function openRentGateway(order: OnlinePaymentResponse, description = 'Rent payment', leaseCurrency?: string | null): Promise<GatewayResult> {
  if (order.gateway === 'cashfree') {
    if (!order.sessionId) return Promise.resolve({ outcome: 'failed', message: 'Payment session missing' })
    return openCashfree(order.sessionId, order.environment)
  }
  if (order.gateway === 'razorpay') {
    if (!order.key || !order.orderId) return Promise.resolve({ outcome: 'failed', message: 'Payment order missing' })
    const options: RazorpayOptions = { key: order.key, order_id: order.orderId, amount: order.amount, currency: order.currency || leaseCurrency || 'INR', description, prefill: order.prefill, config: order.config, retry: order.retry }
    return openRazorpay(options)
  }
  return Promise.resolve({ outcome: 'failed', message: `Unsupported gateway: ${order.gateway}` })
}

// The signed-in account's email, given to Paddle's checkout (LP-542).
const accountEmail = () => useSessionStore.getState().user?.email || undefined

/** A lease fee or agreement order: Razorpay, Paddle where it bills (LP-514), or Cashfree where it takes them (LP-521). */
export function openOrderGateway(init: RazorpayOrderInit, description: string, prefill?: Record<string, string>): Promise<GatewayResult> {
  if (init.gateway === 'cashfree') {
    if (!init.sessionId) return Promise.resolve({ outcome: 'failed', message: 'Payment session missing' })
    return openCashfree(init.sessionId, init.environment)
  }
  if (init.gateway === 'paddle') return openPaddle(init.gatewayOrderId, init.clientToken, init.environment, prefill?.email || accountEmail())
  return openRazorpay({ key: init.key, order_id: init.gatewayOrderId, amount: init.amount, currency: init.currency, description, prefill })
}

/** A Paddle plan opens its transaction (LP-514); a free plan has no key and needs no checkout. */
export function openSubscriptionGateway(res: SubscriptionUpgradeResponse): Promise<GatewayResult> {
  if (res.provider === 'paddle') return openPaddle(res.transactionId, res.clientToken, res.environment, res.prefill?.email || accountEmail())
  if (!res.key) return Promise.resolve({ outcome: 'success' })
  const prefill: Record<string, string> = {}
  if (res.prefill?.contact) prefill.contact = res.prefill.contact
  if (res.prefill?.email) prefill.email = res.prefill.email
  return openRazorpay({ key: res.key, subscription_id: res.subscription_id, name: res.name || 'LeazePro', description: res.description, prefill })
}
